import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { useNavigate } from 'react-router-dom';
import { startPaymentProcessing, paymentFailure, resetCheckoutState } from '../store/checkoutSlice';
import { removeFromCart } from '../store/cartSlice';
import Spinner from '../components/Spinner';
import { useTranslation } from 'react-i18next';
import { HiTrash } from 'react-icons/hi';

// Fonction simple pour supprimer les balises HTML (avec type)
const stripHtml = (html: string | null | undefined): string => {
   if (!html) return '';
   // Crée un élément temporaire, y insère le HTML, puis récupère le texte brut
   try {
       const doc = new DOMParser().parseFromString(html, 'text/html');
       return doc.body.textContent || "";
   } catch (e) {
       console.error("Erreur lors du nettoyage HTML:", e);
       // Fallback vers une regex simple si DOMParser échoue
       return html.replace(/<[^>]*>/g, ''); 
   }
}

const Checkout = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { isProcessing, error } = useSelector((state: RootState) => state.checkout);
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const [coupon, setCoupon] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
      dispatch(resetCheckoutState());
  }, [dispatch]);

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.posterConfiguration.product.price, 0);
  };

   const formatPrice = (price: number) => {
    return price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    dispatch(startPaymentProcessing());

    const lineItems = cartItems.map((item, index) => {
      const plainTextTitle = stripHtml(item.posterConfiguration.labels.title.text || t('cart.defaultTitle', 'Affiche personnalisée'));
      // Créer le nom préfixé et le mettre en majuscules
      const prefixedTitle = `${t('checkout.posterPrefix', 'Poster')} ${index + 1}: ${plainTextTitle}`.toUpperCase();

      return {
          price_data: {
            currency: 'eur',
            product_data: {
              name: prefixedTitle, // Utiliser le titre préfixé et en majuscules
              metadata: { cartItemId: item.id }
            },
            unit_amount: Math.round(item.posterConfiguration.product.price * 100),
          },
          quantity: 1,
        };
    });

    try {
      console.log("Calling backend to create Stripe Checkout Session...");
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      // Préparer le body avec ou sans coupon
      const body: any = { line_items: lineItems };
      if (coupon.trim() !== "") {
        body.coupon = coupon.trim();
      }
      const response = await fetch(`${backendUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        let errorMessage = t('checkout.sessionError', 'Failed to create checkout session') + ` (${response.status} ${response.statusText})`;
        try {
            const errorData = await response.json();
            errorMessage = errorData?.error?.message || errorData?.message || errorMessage;
        } catch (e) {
            console.warn("Could not parse error response as JSON.");
        }
        throw new Error(errorMessage);
      }

      const successData = await response.json();
      const { url } = successData;
      console.log("Received Stripe Checkout Session URL:", url);

      if (!url) {
           throw new Error(t('checkout.missingSessionUrl', 'Missing session URL from server response.'));
      }

      window.location.href = url;

    } catch (err: any) {
       console.error("Checkout session creation error:", err);
       dispatch(paymentFailure(err.message || t('checkout.unexpectedError', 'An unexpected error occurred.')));
    }
  };

  const handleRemove = (id: string) => {
    dispatch(removeFromCart(id));
    // Si le panier est vide après suppression, redirige vers /cart
    setTimeout(() => {
      const state = (window as any).store?.getState?.() || {};
      const items = state.cart?.items || [];
      if (items.length === 0) {
        navigate('/cart');
      }
    }, 100); // Laisse le temps au Redux de mettre à jour
  };

  if (cartItems.length === 0 && !isProcessing) {
       return (
            <div className="text-center py-10 text-gray-500">
                {t('checkout.emptyCart', 'Votre panier est vide. Impossible de procéder au paiement.')}
                <button onClick={() => navigate('/')} className="mt-4 block mx-auto text-blue-500 hover:text-blue-400">{t('checkout.backToEditor', 'Retour à l\'éditeur')}</button>
            </div>
       )
  }

  return (
    <div className="space-y-6 px-1 text-white max-w-2xl mx-auto">
      {/* Header */}
      <div className="space-y-1 mb-6">
        <h1 className="text-2xl font-semibold font-sans">{t('checkout.title', 'Paiement')}</h1>
        <p className="text-gray-400 font-light text-sm">
          {t('checkout.subtitle', 'Merci de vérifier votre commande avant de procéder au paiement.')}
        </p>
      </div>

      {/* Liste des articles */}
      <div className="space-y-5">
        {cartItems.map((item) => (
          <div
            key={item.id}
            className="flex flex-col bg-[#333333] rounded-sm shadow-lg overflow-hidden border border-gray-700"
          >
            <div
              className="w-full rounded-t-sm flex items-center justify-center py-5"
              style={{
                aspectRatio: "0.707",
                minHeight: 120,
                maxHeight: 260,
                background: "#222"
              }}
            >
              {item.thumbnailUrl ? (
                <img
                  src={item.thumbnailUrl}
                  alt="Aperçu de l'affiche"
                  className="w-full h-full object-contain"
                />
              ) : (
                <span className="text-center">Aperçu indisponible</span>
              )}
            </div>
            <div className="p-4 flex flex-col flex-grow">
              <div 
                className="font-semibold font-sans text-lg mb-1 truncate cart-item-title"
                dangerouslySetInnerHTML={{ __html: item.posterConfiguration.labels.title.text || 'Affiche personnalisée' }}
              />
              <div
                className="text-xs text-gray-400 font-light leading-snug mb-3 cart-item-details"
                dangerouslySetInnerHTML={{ __html: item.posterConfiguration.labels.description?.text || '' }}
              />
              <div className="mt-auto pt-2 flex items-center justify-between">
                <p className="text-base font-semibold font-sans">{formatPrice(item.posterConfiguration.product.price)}</p>
                <button
                  onClick={() => handleRemove(item.id)}
                  className="text-gray-500 hover:text-red-500 transition-colors p-1 cursor-pointer"
                  aria-label="Supprimer l'article"
                >
                 <HiTrash className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Résumé et bouton paiement */}
      <div className="pt-8 mt-8 border-t border-gray-600 space-y-5">
        <div className="flex justify-between items-center text-base font-semibold font-sans">
          <span>{t('cart.total', 'Total :')}</span>
          <span>{formatPrice(getTotalPrice())}</span>
        </div>
        {/* Section Code promo multilingue */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={coupon}
              onChange={e => setCoupon(e.target.value)}
              placeholder={t('checkout.couponPlaceholder', 'Code promo')}
              className="flex-1 px-3 py-2 rounded bg-[#222] text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400"
              aria-label={t('checkout.couponLabel', 'Code promo')}
            />
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm font-medium cursor-pointer"
            >
              {t('checkout.pay', 'Payer')}
            </button>
          </div>
          {/* Affichage d'un message d'erreur ou de succès pour le code promo si besoin */}
          {/*
          {couponError && (
            <div className="text-red-500 text-xs mt-1">{t('checkout.couponError', 'Code promo invalide ou expiré.')}</div>
          )}
          {couponSuccess && (
            <div className="text-green-500 text-xs mt-1">{t('checkout.couponSuccess', 'Code promo appliqué !')}</div>
          )}
          */}
        </form>
        {error && (
          <div className="text-red-500 text-sm mt-2">{t('checkout.paymentError', 'Erreur de paiement :')} {error}</div>
        )}
      </div>
    </div>
  );
};

export default Checkout;